/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useRef, useState } from 'react';
import { Braces2, InfoCircle, ArrowsMinimize2 } from '@saleshandy/icons';
import { v4 as uuidv4 } from 'uuid';
import type { IProps } from './types';
import Modal from '../../../../../../../../../shared/design-system/components/atoms/modal';
import Editor from '../../../../../../../../../shared/editor/editor';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../../../../shared/design-system/components/overlay';
import { Variant } from '../../../../../../../../../shared/design-system/components/atoms/button/button';
import OverlayMergeTags from '../../../../../../../../../shared/components/overlay-merge-tags';
import { insertChipForMergeTag } from '../../../../../../../../../shared/editor/helpers';

const TaskNoteModal: React.FC<IProps> = ({
  show,
  onClose,
  onSubmit,
  onContentBlur,
  taskNote,
  mergeTagOptions,
  title,
  maxLength,
  contentAreaPlaceholder,
  isExpandOptionVisible = false,
  useEditor = false,
}) => {
  const editorRef = useRef<any>(null);

  const [innerHTML, setInnerHTML] = useState(taskNote);

  const renderMergeTagSelection = () => (
    <OverlayMergeTags
      options={mergeTagOptions}
      onChange={(option) => {
        const { tag } = option;
        const uuid = uuidv4();
        editorRef.current?.insertContent(
          insertChipForMergeTag(tag, uuid, false, true, option?.fieldType),
        );
      }}
      placement={Placement.BottomEnd}
      searchPlaceholder="Search Tags"
      className="merge-tags-dropdown"
    >
      <div
        onMouseDown={(evt) => {
          evt.preventDefault(); // Avoids loosing focus from the editable area
        }}
        className="gray-16 merge-tag bs-border-radius-50 pointer"
      >
        <Braces2 width={20} height={20} className="pointer" />
      </div>
    </OverlayMergeTags>
  );

  const handleAddMergeTagInContent = (option) => {
    const { tag } = option;
    const uuid = uuidv4();
    editorRef.current?.insertContent(
      insertChipForMergeTag(tag, uuid, false, true, option?.fieldType),
    );
  };

  const handleAddVariableTagInContent = (tag) => {
    editorRef.current?.insertContent(tag);
  };

  const getTooltipText = () =>
    title && title?.toLowerCase() !== 'task note'
      ? 'Write personalized InMail message'
      : 'Add instructions/tips for task assignee to execute this task successfully.';

  return (
    <Modal
      show={show}
      titleContent={
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center pl-4">
            <span className="semibold-3 gray-txt-10">
              {title || 'Task Instruction'}
            </span>
            <OverlayTooltip text={getTooltipText()} className="tooltip-mdd">
              <InfoCircle width={16} height={16} className="gray-txt-11 ml-1" />
            </OverlayTooltip>
          </div>
          {renderMergeTagSelection()}
        </div>
      }
      backdrop="static"
      className="task-note-modal"
      submitButtonText="Save"
      onClose={onClose}
      onHide={onClose}
      onSubmit={() => onSubmit(innerHTML)}
      isCustomCloseIcon={false}
      isSubmitDisabled={innerHTML?.length > 3000}
      cancelButtonVarient={Variant.Outlined}
      extraModalProps={{
        centered: true,
        enforceFocus: false,
        'aria-labelledby': 'contained-modal-title-vcenter',
      }}
    >
      <div className="task-note-modal-editor">
        {useEditor && (
          <div className="d-flex justify-content-end position-absolute expand message-expand-modal">
            <span className="regular-1 gray-txt-11">
              <ArrowsMinimize2
                width={16}
                height={16}
                className="gray-txt-15 pointer"
                onClick={onClose}
              />
            </span>
          </div>
        )}
        <Editor
          value={innerHTML}
          onEditorChange={(content) => setInnerHTML(content)}
          error={false}
          className="sequence-step-editor"
          showMinimalToolbar={false}
          showAddAttachments={false}
          showMergeAndVariableTags={true}
          mergeTags={mergeTagOptions}
          addMergeTagInContent={handleAddMergeTagInContent}
          addVariableTagInContent={handleAddVariableTagInContent}
          getEditorInstance={(editor) => {
            editorRef.current = editor;
          }}
          showCount
          maxInputCount={maxLength || 3000}
          placeholder={contentAreaPlaceholder}
          isTemplateEditorModal={false}
          hideHeader={true}
          onEditorBlur={onContentBlur}
        />
      </div>
    </Modal>
  );
};

export default TaskNoteModal;
